module.exports = function installDateTimePickerBridge($, moment) {
  const td = require('@eonasdan/tempus-dominus/dist/js/tempus-dominus.js');
  require('@eonasdan/tempus-dominus/dist/locales/de.js');
  require('@eonasdan/tempus-dominus/dist/locales/es.js');
  require('@eonasdan/tempus-dominus/dist/locales/fr.js');
  require('@eonasdan/tempus-dominus/dist/locales/it.js');
  require('@eonasdan/tempus-dominus/dist/locales/nl.js');
  require('@eonasdan/tempus-dominus/dist/locales/pl.js');
  require('@eonasdan/tempus-dominus/dist/locales/pt-PT.js');
  require('@eonasdan/tempus-dominus/dist/locales/ru.js');
  require('@eonasdan/tempus-dominus/dist/locales/sk.js');
  require('@eonasdan/tempus-dominus/dist/locales/tr.js');
  require('@eonasdan/tempus-dominus/dist/locales/uk.js');
  require('@eonasdan/tempus-dominus/dist/locales/zh-CN.js');
  require('@eonasdan/tempus-dominus/dist/locales/zh-TW.js');

  window.tempusDominus = td;

  const languageMap = {
    pt: 'pt-PT',
    zh: 'zh-CN'
  };

  const iconMap = {
    clear: 'fa-solid fa-trash',
    close: 'fa-solid fa-xmark',
    date: 'fa-solid fa-calendar',
    down: 'fa-solid fa-chevron-down',
    next: 'fa-solid fa-chevron-right',
    previous: 'fa-solid fa-chevron-left',
    time: 'fa-regular fa-clock',
    today: 'fa-solid fa-calendar-day',
    up: 'fa-solid fa-chevron-up'
  };

  function normalizeLanguage(language) {
    return languageMap[language] || language || 'en';
  }

  function toMomentFormat(format) {
    return format || 'DD-MM-YYYY';
  }

  function toTempusFormat(format) {
    return toMomentFormat(format)
      .replace(/YYYY/g, 'yyyy')
      .replace(/YY/g, 'yy')
      .replace(/DD/g, 'dd')
      .replace(/\bD\b/g, 'd')
      .replace(/A/g, 'T');
  }

  function toDateTime(value, format, locale) {
    if (!value) {
      return null;
    }

    if (moment.isMoment(value)) {
      return td.DateTime.convert(value.toDate(), locale);
    }

    if (value instanceof Date) {
      return td.DateTime.convert(value, locale);
    }

    if (typeof value === 'string') {
      const parsed = moment(value, format, true);
      if (parsed.isValid()) {
        return td.DateTime.convert(parsed.toDate(), locale);
      }
    }

    return null;
  }

  function ensureRegistry() {
    if (!window.__legacyDatePickerInstances) {
      window.__legacyDatePickerInstances = [];
    }

    return window.__legacyDatePickerInstances;
  }

  function safeHide(instance, options) {
    if (!instance) {
      return;
    }

    const shouldBlurInput = !options || options.blurInput !== false;
    const input = instance.optionsStore && instance.optionsStore.input;
    instance.__legacySuppressShowUntil = Date.now() + 150;

    if (typeof instance.hide === 'function') {
      instance.hide();
    }

    if (instance.display && instance.display.widget) {
      instance.display.widget.classList.remove('show');
    }

    if (shouldBlurInput && input && document.activeElement === input && typeof input.blur === 'function') {
      window.setTimeout(() => {
        input.blur();
      }, 0);
    }
  }

  function getWidget(instance) {
    return instance && instance.display ? instance.display.widget : null;
  }

  function isTargetInsideWidget(target, instance) {
    const widget = getWidget(instance);
    return Boolean(widget && target && widget.contains(target));
  }

  function shouldKeepPickerOpen(target, element, instance) {
    return Boolean(
      isTargetInsideWidget(target, instance) ||
        target.closest('[data-td-target-toggle]') ||
        target === element ||
        (element.closest && target.closest && target.closest('#' + element.id))
    );
  }

  function createLegacyApi($element, instance, format) {
    return {
      date(value) {
        if (arguments.length === 0) {
          const picked = instance.dates.lastPicked;
          return picked ? moment(picked) : null;
        }

        if (value === null) {
          instance.dates.clear();
          return this;
        }

        const nextValue = toDateTime(value, format, instance.optionsStore.options.localization.locale);
        if (nextValue) {
          instance.dates.setValue(nextValue);
        }

        return this;
      },
      hide() {
        instance.hide();
        return this;
      },
      destroy() {
        instance.dispose();
        $element.removeData('DateTimePicker');
      }
    };
  }

  $.fn.datetimepicker = function legacyDateTimePicker(options) {
    return this.each(function initializeLegacyPicker() {
      const $element = $(this);
      const existing = $element.data('DateTimePicker');

      if (existing) {
        return;
      }

      const language = normalizeLanguage(document.documentElement.getAttribute('data-app-language'));
      const legacyFormat = toMomentFormat(options && options.format);
      const tempusFormat = toTempusFormat(options && options.format);
      const enableTime = /H|h|m/.test(legacyFormat);
      const localization = Object.assign({}, td.DefaultEnLocalization, {
        format: tempusFormat,
        locale: language
      });
      const minDate = toDateTime(options && options.minDate, legacyFormat, language);
      const maxDate = toDateTime(options && options.maxDate, legacyFormat, language);
      const pickerOptions = {
        display: {
          buttons: {
            clear: true,
            close: true,
            today: true
          },
          components: {
            calendar: true,
            clock: enableTime,
            date: true,
            decades: true,
            hours: enableTime,
            minutes: enableTime,
            month: true,
            seconds: false,
            useTwentyfourHour: true,
            year: true
          },
          icons: iconMap
        },
        localization,
        allowInputToggle: true,
        useCurrent: true
      };

      if (language !== 'en') {
        td.locale(language);
      }

      if (minDate || maxDate) {
        pickerOptions.restrictions = {};
        if (minDate) {
          pickerOptions.restrictions.minDate = minDate;
        }
        if (maxDate) {
          pickerOptions.restrictions.maxDate = maxDate;
        }
      }

      const instance = new td.TempusDominus(this, pickerOptions);
      const $group = $element.closest('.input-group');
      const $toggle = $group.find('.input-group-text').first();
      let reopenTimer = null;
      const showPicker = () => {
        if (instance.__legacySuppressShowUntil && Date.now() < instance.__legacySuppressShowUntil) {
          return;
        }
        window.clearTimeout(reopenTimer);
        reopenTimer = window.setTimeout(() => {
          if (instance.__legacySuppressShowUntil && Date.now() < instance.__legacySuppressShowUntil) {
            return;
          }
          if (!instance.display._isVisible) {
            instance.show();
          }
        }, 0);
      };

      $element.on('focus click', showPicker);
      if ($toggle.length) {
        $toggle.on('mousedown click', (event) => {
          event.preventDefault();
          showPicker();
        });
      }

      ensureRegistry().push({ element: this, instance });
      instance.subscribe(td.Namespace.events.change, (event) => {
        $element.trigger('input');
        $element.trigger('change');
        const validator = $element.closest('form').data('validator');
        if (validator && $element.is(':visible') && !$element.is('[disabled]')) {
          validator.element($element[0]);
        }
        if (!enableTime && event && event.date) {
          safeHide(instance);
        }
      });

      const initialValue = toDateTime($element.val(), legacyFormat, language);
      if (initialValue) {
        instance.dates.setValue(initialValue);
      }

      $element.data('DateTimePicker', createLegacyApi($element, instance, legacyFormat));
    });
  };

  if (!window.__legacyDatePickerOutsideClickBound) {
    const hideOtherPickers = (target) => {
      ensureRegistry().forEach(({ element, instance }) => {
        if (shouldKeepPickerOpen(target, element, instance)) {
          return;
        }

        safeHide(instance);
      });
    };

    document.addEventListener('pointerdown', (event) => {
      hideOtherPickers(event.target);
    }, true);

    document.addEventListener('click', (event) => {
      hideOtherPickers(event.target);
    }, true);

    document.addEventListener('focusin', (event) => {
      hideOtherPickers(event.target);
    }, true);

    document.addEventListener(
      'click',
      (event) => {
        const action = event.target && event.target.closest('.tempus-dominus-widget [data-action]');
        if (!action) {
          return;
        }

        const actionName = action.getAttribute('data-action');
        if (actionName !== 'today' && actionName !== 'close') {
          return;
        }

        // "Today" can keep the same value and skip the change event.
        window.setTimeout(() => {
          ensureRegistry().forEach(({ instance }) => {
            if (isTargetInsideWidget(action, instance)) {
              safeHide(instance);
            }
          });
        }, 0);
      },
      true
    );

    window.__legacyDatePickerOutsideClickBound = true;
  }
};
